import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AllCategory, getAllCatagory } from "../../../api/category.api";
import { PostMenuBody, postMenu } from "../../../api/menu.api";
import { uploadMenuImage } from "../../../api/image.api";
import { fileToResizedDataUrl } from "../../../utils/image";
import { State } from "../../../reducer";
import CropImageModal from "../../../components/CropImageModal";
import AvailabilityEditor from "../../../components/AvailabilityEditor";
import DailyStockEditor from "../../../components/DailyStockEditor";
import SaucesEditor from "../../../components/SaucesEditor";

function timeToMin(t: string): number | null {
  if (!t) return null;
  const [h, m] = t.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function AddMenu() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const categoryRedux = useSelector((state: State) => state.category);
  const categoryIdAsNumber = categoryId ? parseInt(categoryId) : categoryRedux.category_id;

  const [categories, setCategories] = useState<AllCategory>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number>(categoryIdAsNumber);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageFileId, setImageFileId] = useState<string>("");
  const [originalSrc, setOriginalSrc] = useState<string>("");
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openPicker = () => fileInputRef.current?.click();
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string>("");
  const [availDays, setAvailDays] = useState<number[]>([]);
  const [availFrom, setAvailFrom] = useState<string>("");
  const [availTo, setAvailTo]     = useState<string>("");
  const [dailyStock, setDailyStock] = useState<number | null>(null);
  const [sauces, setSauces] = useState<string[] | null>(null);

  // If the URL category is a dessert category, hide the chooser entirely so the
  // flow stays "just add an item" — matches the dessert-management flat list.
  const lockedCategory = useMemo(() => {
    return categories.find((c) => c.id === selectedCategory && c.categoryFor === "dessert") ?? null;
  }, [categories, selectedCategory]);

  useEffect(() => {
    getAllCatagory().then((all) => {
      setCategories(all.filter((c) => c.isAvailable === "available"));
    });
  }, []);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v.charAt(0) === " ") {
      setNameError("Name cannot start with a space");
      return;
    }
    setName(v);
    setNameError("");
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    try {
      // Decode + lightly downscale before opening the cropper, so the editor
      // works on a bounded-size buffer (faster on phones, smaller memory).
      const base64 = await fileToResizedDataUrl(file, 2400, 0.95);
      setOriginalSrc(base64);
      setCropSrc(base64);
    } catch (err) {
      console.error(err);
      toast.error("Could not read image");
    }
    // Allow re-selecting the same file later.
    e.target.value = "";
  };

  const handleCropConfirm = async (cropped: string) => {
    setCropSrc(null);
    setUploading(true);
    try {
      const result = await uploadMenuImage({ file: cropped });
      if (result?.url) {
        setImageUrl(result.url);
        setImageFileId(result.fileId);
      } else {
        toast.error("Image upload failed");
      }
    } catch (err) {
      console.error(err);
      // Show why it failed (size, format, session) instead of a generic line.
      toast.error(err instanceof Error && err.message ? err.message : "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const isFormValid = !!name && !!selectedCategory && !nameError && !uploading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      toast("Please enter a name and select a category");
      return;
    }
    setSubmitting(true);
    try {
      const body: PostMenuBody = {
        name,
        description: description || "-",
        category_id: selectedCategory,
        ...(imageUrl ? { image: imageUrl, imageFileId } : {}),
        ...(availDays.length > 0 ? { availableDays: availDays } : {}),
        availableFromMin: timeToMin(availFrom),
        availableToMin: timeToMin(availTo),
        dailyStock,
        sauces,
      };
      await postMenu(body);
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Could not create menu");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans pb-[120px]">
      <header className="px-5 pt-[24px] pb-[16px] border-b border-ink-100 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-display font-black text-[22px] leading-tight text-ink-1000">New menu</h1>
      </header>

      <form onSubmit={handleSubmit} className="px-5 pt-[20px] space-y-[20px]">
        {/* Image */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Image</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={openPicker}
            className="relative block w-full aspect-[16/10] rounded-2xl border-2 border-dashed border-ink-200 bg-ink-50 overflow-hidden text-left"
          >
            {imageUrl ? (
              <img src={imageUrl} alt="" className="w-full h-full object-cover pointer-events-none" />
            ) : uploading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-ink-500">
                <svg className="w-7 h-7 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <span className="text-xs font-semibold">Uploading…</span>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-ink-500">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                  <svg className="w-6 h-6 text-ink-700" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16l5-5 4 4 3-3 6 6M3 8a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                  </svg>
                </div>
                <span className="text-xs font-semibold">Tap to upload</span>
                <span className="text-[11px] text-ink-400">PNG · JPG · WEBP · max 5 MB</span>
              </div>
            )}
          </button>

          {imageUrl && (
            <div className="grid grid-cols-3 gap-2 mt-2">
              <button
                type="button"
                onClick={() => setCropSrc(originalSrc || imageUrl)}
                className="h-[44px] rounded-xl bg-ink-50 hover:bg-ink-100 text-ink-1000 font-display font-bold text-sm flex items-center justify-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 2v4H2m16 0h-4V2M6 22v-4H2m16 0h-4v4M3 9v6a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Crop
              </button>
              <button
                type="button"
                onClick={openPicker}
                className="h-[44px] rounded-xl bg-ink-50 hover:bg-ink-100 text-ink-1000 font-display font-bold text-sm flex items-center justify-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                </svg>
                Replace
              </button>
              <button
                type="button"
                onClick={() => { setImageUrl(""); setImageFileId(""); setOriginalSrc(""); }}
                className="h-[44px] rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-display font-bold text-sm flex items-center justify-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
                Remove
              </button>
            </div>
          )}
        </section>

        {/* Name */}
        <section>
          <label htmlFor="menu-name" className="block font-display font-bold text-sm text-ink-1000 mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="menu-name"
            type="text"
            value={name}
            maxLength={50}
            placeholder="e.g. Iced Latte"
            onChange={handleNameChange}
            className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent text-base text-ink-1000 placeholder:text-ink-400 focus:outline-none focus:bg-white focus:border-ink-1000 transition-colors"
          />
          {nameError && <p className="text-xs text-red-500 mt-1.5">{nameError}</p>}
        </section>

        {/* Description */}
        <section>
          <label htmlFor="menu-desc" className="block font-display font-bold text-sm text-ink-1000 mb-2">
            Description
          </label>
          <input
            id="menu-desc"
            type="text"
            value={description}
            maxLength={150}
            placeholder="Optional"
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent text-base text-ink-1000 placeholder:text-ink-400 focus:outline-none focus:bg-white focus:border-ink-1000 transition-colors"
          />
        </section>

        {/* Availability */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Availability</span>
          <div className="rounded-2xl bg-ink-50 p-4">
            <AvailabilityEditor
              days={availDays}
              setDays={setAvailDays}
              fromTime={availFrom}
              toTime={availTo}
              setFromTime={setAvailFrom}
              setToTime={setAvailTo}
            />
          </div>
        </section>

        {/* Daily stock cap */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Daily stock</span>
          <DailyStockEditor value={dailyStock} onChange={setDailyStock} />
        </section>

        {/* Sauce / add-on picker */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Sauce options</span>
          <SaucesEditor value={sauces} onChange={setSauces} />
        </section>

        {/* Category */}
        {lockedCategory ? (
          <section>
            <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Category</span>
            <div className="px-4 h-[52px] rounded-2xl bg-ink-50 flex items-center justify-between">
              <span className="font-display font-bold text-sm text-ink-1000">{lockedCategory.name}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Dessert</span>
            </div>
          </section>
        ) : (
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">
            Category <span className="text-red-500">*</span>
          </span>
          <div className="space-y-2">
            {categories.map((c) => (
              <label
                key={c.id}
                className={`flex items-center gap-3 px-4 h-[52px] rounded-2xl border-2 cursor-pointer transition ${
                  selectedCategory === c.id
                    ? "border-ink-1000 bg-white"
                    : "border-transparent bg-ink-50 hover:border-ink-200"
                }`}
              >
                <input
                  type="radio"
                  name="category"
                  value={c.id}
                  checked={selectedCategory === c.id}
                  onChange={() => setSelectedCategory(c.id)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedCategory === c.id ? "border-ink-1000" : "border-ink-300"
                }`}>
                  {selectedCategory === c.id && <div className="w-2.5 h-2.5 rounded-full bg-ink-1000" />}
                </div>
                <span className="font-display font-semibold text-base text-ink-1000 flex-1">{c.name}</span>
                <span className="text-xs text-ink-500 uppercase tracking-wider font-semibold">{c.categoryFor}</span>
              </label>
            ))}
          </div>
        </section>
        )}
      </form>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-ink-100 px-5 py-3 flex gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-v2 flex-1 h-[48px] bg-ink-100 text-ink-1000 rounded-2xl"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!isFormValid || submitting}
          onClick={handleSubmit}
          className="btn-v2-primary flex-1 h-[48px] rounded-2xl"
        >
          {submitting ? "Saving…" : "Add menu"}
        </button>
      </div>

      {cropSrc && (
        <CropImageModal
          src={cropSrc}
          aspect={16 / 10}
          onCancel={() => setCropSrc(null)}
          onConfirm={handleCropConfirm}
        />
      )}

      <ToastContainer />
    </div>
  );
}

export default AddMenu;
